import { customerListQuerySchema } from '@chukua/common';
import { Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { getCustomer, listCustomers, type CustomerDetail } from '../customers/customers.api';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

interface CustomerOption {
  value: string;
  label: string;
}

function addOptionIfMissing(customer: CustomerDetail, setOptions: (updater: (prev: CustomerOption[]) => CustomerOption[]) => void): void {
  setOptions((prev) => (prev.some((option) => option.value === customer.id) ? prev : [{ value: customer.id, label: customer.name }, ...prev]));
}

interface CustomerSearchSelectProps {
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

/**
 * Remote-search customer picker: `GET /customers?q=`, debounced. Customers can run past
 * the ~100-row one-shot-fetch pattern `TransferOwnershipModal` uses for owners, so this
 * one searches instead. When `value` is set but not yet among the fetched options (a
 * job being reopened), it fetches that one customer to show its name rather than its id.
 */
export function CustomerSearchSelect({ id, value, onChange, disabled }: CustomerSearchSelectProps) {
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Membership is checked inside the updater, against the latest options, so this only depends on `value` — a search result doesn't retrigger it.
  useEffect(() => {
    if (!value) return;
    getCustomer(value).then((customer) => addOptionIfMissing(customer, setOptions));
  }, [value]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  function search(term: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const page = await listCustomers(customerListQuerySchema.parse({ q: term, pageSize: PAGE_SIZE }));
        setOptions(page.items.map((customer) => ({ value: customer.id, label: customer.name })));
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }

  return (
    <Select
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      showSearch
      filterOption={false}
      loading={loading}
      onSearch={search}
      options={options}
      placeholder="Search customers by name"
      notFoundContent={loading ? 'Searching…' : 'No customers found'}
    />
  );
}
